import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, CheckIcon } from "@heroicons/react/24/outline";
import { homepageApi, useHomepageResource } from "../hooks/useHomepageResource";
import styles from "./Hero.module.css";
import HeroExamPrompt from "./HeroExamPrompt";
import RecentUsageBadge from "./RecentUsageBadge";

export type HomepageBank = "sdle" | "smle";
const validateCount = (bank: HomepageBank) => (input: unknown): number => {
  const data = input as { bank_code?: string; published_count?: number };
  if (data?.bank_code !== bank || !Number.isInteger(data.published_count) || data.published_count! < 0) throw new Error("Invalid bank count");
  return data.published_count!;
};
const validateSdle = validateCount("sdle");
const validateSmle = validateCount("smle");
type Props = { demo?: React.ReactNode; composer?: React.ReactNode };
export default function Hero({ demo, composer }: Props) {
  const sdle = useHomepageResource(`${homepageApi}/api/qbank/stats?bank=sdle`, validateSdle);
  const smle = useHomepageResource(`${homepageApi}/api/qbank/stats?bank=smle`, validateSmle);
  const banks = { sdle, smle };

  return <div className={styles.heroBand}><section className={styles.hero} aria-labelledby="hero-title" id="choose-exam">
    <div className={styles.copy}>
      <RecentUsageBadge />
      <h1 id="hero-title">SMLE <em>and</em> SDLE exam prep</h1>
      <HeroExamPrompt />
      <ul className={styles.benefits} aria-label="Your study tools">
        <li><CheckIcon aria-hidden="true" /><div><strong>QBank answers with textbook references, <em><u>updated monthly!</u></em></strong><span>Study by specialty with textbook-backed explanations.</span></div></li>
        <li><CheckIcon aria-hidden="true" /><div><strong>Mock Exams To Test Your Readiness</strong><span>Practice by specialty and track your progress.</span></div></li>
        <li className={styles.guaranteeBenefit}><CheckIcon aria-hidden="true" /><div><strong>Money-Back Guarantee</strong><span>Don’t pass your SMLE or SDLE? Get your money back. <Link to="/terms#money-back-guarantee">See terms</Link>.</span></div></li>
      </ul>
      {composer && <section className={styles.aiComposer} aria-label="Ask C-Shaped AI"><h2 className={styles.aiHeading}>Try reference-based AI</h2><ol className={styles.composerSteps}>
        <li>Choose medicine or dentistry.</li>
        <li>Copy and paste your SMLE and SDLE questions.</li>
        <li><strong>Get answers in seconds,</strong> with textbook references.</li>
      </ol>{composer}</section>}
      <div className={styles.conversion} aria-label="Choose your exam">
        <div className={styles.examOptions}>
          {(["sdle", "smle"] as HomepageBank[]).map(bank => {
            const count = banks[bank];
            const available = count.status === "ready" && count.data! > 0;
            return <div className={styles.examOption} key={bank}>
              <div className={styles.examIdentity}><strong>{bank.toUpperCase()}</strong><span>{bank === "sdle" ? "Dentistry" : "Medicine"}</span></div>
              <p className={styles.availability} aria-live="polite">{count.status === "loading" ? "Checking questions…" : count.status === "error" ? "Couldn't load questions" : <><b>{count.data!.toLocaleString("en-US")}</b> questions</>}</p>
              {count.status === "ready" && count.data === 0 && <span className={styles.emptyNote}>Questions aren't available yet</span>}
              <Link className={`${styles.examLink} ${bank === "sdle" ? styles.primary : ""}`} to={`/qbank/${bank}`}>{available ? `Start ${bank.toUpperCase()} practice` : `Explore ${bank.toUpperCase()}`}<ArrowRightIcon aria-hidden="true" /></Link>
              {count.status === "error" && <button type="button" className={styles.retry} onClick={count.retry}>Retry {bank.toUpperCase()} questions</button>}
            </div>;
          })}
        </div>
      </div>

    </div>
    {demo && <div className={styles.visual}>{demo}</div>}
  </section></div>;
}
